import { z } from 'zod';

// Enums matching Prisma schema
export const ContentTypeEnum = z.enum([
  'TEXT',
  'TEXTAREA',
  'RICHTEXT',
  'IMAGE',
  'URL',
  'EMAIL',
  'PHONE',
  'NUMBER',
  'BOOLEAN',
  'COLOR',
  'DATE',
  'JSON'
]);

export const ChangeTypeEnum = z.enum(['CREATE', 'UPDATE', 'DELETE']);

// Content Category Schema
export const ContentCategorySchema = z.object({
  id: z.string().cuid(),
  name: z.string().min(1).max(50).regex(/^[a-z-_]+$/, "Only lowercase letters, hyphens, and underscores"),
  displayName: z.string().min(1).max(100),
  description: z.string().optional(),
  order: z.number().int().min(0).default(0),
  isActive: z.boolean().default(true),
  createdAt: z.date(),
  updatedAt: z.date(),
});

// Enhanced Site Content Schema
export const SiteContentSchema = z.object({
  id: z.string().cuid(),
  key: z.string().min(1).max(200).regex(/^[a-z0-9._-]+$/, "Only lowercase letters, numbers, dots, hyphens, and underscores"),
  value: z.string(),
  type: ContentTypeEnum.default('TEXT'),
  categoryId: z.string().cuid(),

  // Metadata
  displayName: z.string().min(1).max(200),
  description: z.string().optional(),
  placeholder: z.string().optional(),
  order: z.number().int().min(0).default(0),
  isRequired: z.boolean().default(false),
  isActive: z.boolean().default(true),

  // Validation rules
  minLength: z.number().int().min(0).optional(),
  maxLength: z.number().int().min(1).optional(),
  pattern: z.string().optional(),

  // Image-specific metadata (only used when type = IMAGE)
  altText: z.string().max(500).optional(),
  width: z.number().int().min(1).max(10000).optional(),
  height: z.number().int().min(1).max(10000).optional(),
  fileSize: z.number().int().min(1).optional(),
  mimeType: z.enum(['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/svg+xml', 'image/gif', 'image/ico']).optional(),
  tags: z.string().max(1000).optional(),

  createdAt: z.date(),
  updatedAt: z.date(),
});

// Content with Category relation
export const SiteContentWithCategorySchema = SiteContentSchema.extend({
  category: ContentCategorySchema,
});

// Content History Schema
export const ContentHistorySchema = z.object({
  id: z.string().cuid(),
  contentId: z.string().cuid(),
  oldValue: z.string(),
  newValue: z.string(),
  changedBy: z.string().optional(),
  changeType: ChangeTypeEnum.default('UPDATE'),
  createdAt: z.date(),
});

// Input/Output schemas for API operations

// Create Category
export const CreateContentCategorySchema = z.object({
  name: z.string().min(1).max(50).regex(/^[a-z-_]+$/),
  displayName: z.string().min(1).max(100),
  description: z.string().optional(),
  order: z.number().int().min(0).optional(),
});

// Update Category
export const UpdateContentCategorySchema = CreateContentCategorySchema.partial();

// Create Content
export const CreateSiteContentSchema = z.object({
  key: z.string().min(1).max(200).regex(/^[a-z0-9._-]+$/),
  value: z.string(),
  type: ContentTypeEnum.default('TEXT'),
  categoryId: z.string().cuid(),
  displayName: z.string().min(1).max(200),
  description: z.string().optional(),
  placeholder: z.string().optional(),
  order: z.number().int().min(0).optional(),
  isRequired: z.boolean().optional(),
  minLength: z.number().int().min(0).optional(),
  maxLength: z.number().int().min(1).optional(),
  pattern: z.string().optional(),
  // Image-specific fields
  altText: z.string().max(500).optional(),
  width: z.number().int().min(1).max(10000).optional(),
  height: z.number().int().min(1).max(10000).optional(),
  fileSize: z.number().int().min(1).optional(),
  mimeType: z.enum(['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/svg+xml', 'image/gif', 'image/ico']).optional(),
  tags: z.string().max(1000).optional(),
});

// Update Content
export const UpdateSiteContentSchema = CreateSiteContentSchema.partial().omit({ categoryId: true });

// Bulk Update (legacy compatibility)
export const BulkUpdateContentSchema = z.object({
  contents: z.array(z.object({
    id: z.string().cuid(),
    value: z.string(),
  })),
});

// Query schemas
export const ContentQuerySchema = z.object({
  categoryId: z.string().cuid().optional(),
  type: ContentTypeEnum.optional(),
  search: z.string().optional(),
  isActive: z.boolean().optional(),
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(50),
  orderBy: z.enum(['key', 'displayName', 'order', 'createdAt', 'updatedAt']).default('order'),
  sortOrder: z.enum(['asc', 'desc']).default('asc'),
});

// Export types
export type ContentType = z.infer<typeof ContentTypeEnum>;
export type ChangeType = z.infer<typeof ChangeTypeEnum>;
export type ContentCategory = z.infer<typeof ContentCategorySchema>;
export type SiteContent = z.infer<typeof SiteContentSchema>;
export type SiteContentWithCategory = z.infer<typeof SiteContentWithCategorySchema>;
export type ContentHistory = z.infer<typeof ContentHistorySchema>;

export type CreateContentCategoryInput = z.infer<typeof CreateContentCategorySchema>;
export type UpdateContentCategoryInput = z.infer<typeof UpdateContentCategorySchema>;
export type CreateSiteContentInput = z.infer<typeof CreateSiteContentSchema>;
export type UpdateSiteContentInput = z.infer<typeof UpdateSiteContentSchema>;
export type BulkUpdateContentInput = z.infer<typeof BulkUpdateContentSchema>;
export type ContentQueryInput = z.infer<typeof ContentQuerySchema>;

// Response types for API
export type ContentListResponse = {
  data: SiteContentWithCategory[];
  total: number;
  page: number;
  totalPages: number;
};

export type CategoryListResponse = {
  data: ContentCategory[];
  total: number;
};
